/**
 * Point d'entrée du portail (LOT 5.5).
 *
 * Deux mondes :
 *   · les parcours d'AUTHENTIFICATION restent des documents autonomes
 *     (état neuf garanti à chaque entrée/sortie) ;
 *   · tout le reste vit dans le PERSISTENT APP SHELL : la sidebar, le
 *     header, la palette et le profil sont montés une fois — seule la zone
 *     de contenu change (routeur client, plus aucun rechargement de page).
 *
 * ⚠ AUTH_ENFORCEMENT_ENABLED = false : aucune redirection forcée vers
 * /login n'est câblée ici — ce sera la décision explicite du LOT 6.
 */
import '@fontsource-variable/plus-jakarta-sans'
import '@fontsource-variable/jetbrains-mono'
import './design/live-control/tokens.css'
import './design/live-control/intensity.css'
import './design/live-control/base.css'
import './design/live-control/components.css'
import './design/live-control/global-dashboard.css'
import './design/live-control/admin/admin.css'
import './design/live-control/hubs/hubs.css'
import './design/live-control/rh/rh.css'
import './design/live-control/soirees/soirees.css'
import './design/live-control/communication/communication.css'
import './design/live-control/access/access.css'
import './design/live-control/finance/finance.css'
import './design/live-control/associations/associations.css'
import './design/live-control/supervision/supervision.css'
import './design/live-control/interphone/interphone.css'
import './design/live-control/billetterie/billetterie.css'
import './design/live-control/billetterie/soirees-maison.css'
import './design/live-control/billetterie/assistant-soiree.css'
import './design/live-control/layout/layout.css'
import './design/live-control/shell/shell.css'

import { useEffect } from 'react'
import { usePath, isAuthPath, redirect, useNavigationCount } from './design/live-control/router.js'
import { AppShell } from './design/live-control/shell/AppShell.js'
import { GardeEcran } from './design/live-control/shell/GardeEcran.js'
import { GlobalDashboardPage } from './design/live-control/GlobalDashboard.js'
import { RhHubPage } from './design/live-control/rh/RhHub.js'
import { RhPeoplePage } from './design/live-control/rh/PeoplePage.js'
import { RhPersonPage } from './design/live-control/rh/PersonPage.js'
import { HrOrgHubPage, HrPositionsPage, HrPositionDetailPage, HrFunctionsPage, HrFunctionDetailPage, HrLocationsPage, HrTempsHomePage } from './design/live-control/rh/OrgReset.js'
import { SkelloIntegrationPage } from './design/live-control/admin/SkelloIntegrationPage.js'
import { HrTeamsPage, HrTeamDetailPage, HrSkillsPage, HrOrgChartPage } from './design/live-control/rh/OrganisationLot2.js'
import { PlanningPage } from './design/live-control/rh/PlanningPage.js'
import { HrPointeuseAdminPage, HrCongesPage, HrDisposPage } from './design/live-control/rh/TempsPages.js'
import { HrPresencePage } from './design/live-control/rh/PresenceTempsReel.js'
import { HrSignaturesPage, HrAnnoncesPage } from './design/live-control/rh/SignaturesAnnonces.js'
import { ServiceCredentialsPage } from './design/live-control/admin/ServiceCredentials.js'
import { HrValidationWorkstation } from './design/live-control/rh/ValidationWorkstation.js'
import { HrCountersHomePage, HrCountersPersonPage } from './design/live-control/rh/CountersPages.js'
import { MoiPage } from './design/live-control/rh/MoiPage.js'
import { ActivityCenterPage } from './design/live-control/admin/ActivityCenter.js'
import { MobileAuthStartPage, MobileAuthCallbackPage } from './design/live-control/MobileAuthPages.js'
import { RulesPage } from './design/live-control/admin/RulesPage.js'
import { RuleBuilderPage } from './design/live-control/admin/RuleBuilder.js'
import { BackupsPage } from './design/live-control/admin/BackupsPage.js'
import { CommunicationsPage } from './design/live-control/admin/CommunicationsPage.js'
import { TemplateEditorPage } from './design/live-control/admin/TemplateEditor.js'
import { AuditPage } from './design/live-control/admin/AuditPage.js'
import { SettingsHome } from './design/live-control/admin/SettingsHome.js'
import { SondeIntegrationPage } from './design/live-control/supervision/SondeIntegrationPage.js'
import { InterphonePage } from './design/live-control/interphone/InterphonePage.js'
import { InterphoneAppelPage } from './design/live-control/interphone/InterphoneAppelPage.js'
import { InterphoneReglagesPage } from './design/live-control/interphone/InterphoneReglagesPage.js'
import { InterphoneIntegrationPage } from './design/live-control/interphone/InterphoneIntegrationPage.js'
import { BilletterieIntegrationPage } from './design/live-control/billetterie/BilletterieIntegrationPage.js'
import { SoireesMaisonPage } from './design/live-control/billetterie/SoireesMaisonPage.js'
import { SoireeMaisonPage } from './design/live-control/billetterie/SoireeMaisonPage.js'
import { ReglagesBilletteriePage } from './design/live-control/billetterie/ReglagesBilletteriePage.js'
import { SupervisionOverviewPage } from './design/live-control/supervision/SupervisionOverviewPage.js'
import { SupervisionGroupePage } from './design/live-control/supervision/reglages/SupervisionGroupePage.js'
import { SupervisionJournalPage } from './design/live-control/supervision/reglages/SupervisionJournalPage.js'
import { SupervisionConsommationPage } from './design/live-control/supervision/reglages/SupervisionConsommationPage.js'
import { ModeSoireePage } from './design/live-control/supervision/vues/live/ModeSoireePage.js'
import { ListeIncidentsPage } from './design/live-control/supervision/vues/fiches/ListeIncidentsPage.js'
import { FicheIncidentPage } from './design/live-control/supervision/vues/fiches/FicheIncidentPage.js'
import { FicheAppareilPage } from './design/live-control/supervision/vues/fiches/FicheAppareilPage.js'
import { FicheSallePage } from './design/live-control/supervision/vues/fiches/FicheSallePage.js'
import { NuitsPage } from './design/live-control/supervision/vues/nuits/NuitsPage.js'
import { NuitPage } from './design/live-control/supervision/vues/nuits/NuitPage.js'
import { ServeurPage } from './design/live-control/supervision/vues/serveur/ServeurPage.js'
import { RegistrePage } from './design/live-control/supervision/vues/registre/RegistrePage.js'
import { ReseauPage } from './design/live-control/supervision/vues/reseau/ReseauPage.js'
import { VideoPage } from './design/live-control/supervision/vues/video/VideoPage.js'
import { AudiovisuelPage } from './design/live-control/supervision/vues/audiovisuel/AudiovisuelPage.js'
import { BatimentPage } from './design/live-control/supervision/vues/batiment/BatimentPage.js'
import { OnduleursPage } from './design/live-control/supervision/vues/batiment/OnduleursPage.js'
import { TablettesPage } from './design/live-control/tablettes/TablettesPage.js'
import { TablettePage } from './design/live-control/tablettes/TablettePage.js'
import { EnrolementPage } from './design/live-control/tablettes/EnrolementPage.js'
import { TablettesIntegrationPage } from './design/live-control/tablettes/TablettesIntegrationPage.js'
import { UsersPage } from './design/live-control/admin/UsersPage.js'
import { UserDetailPage } from './design/live-control/admin/UserDetailPage.js'
import { RolesPage } from './design/live-control/admin/RolesPage.js'
import { ServicesPage } from './design/live-control/admin/ServicesPage.js'
import { SystemPage } from './design/live-control/admin/MiscPages.js'
import { IntegrationsCatalogPage } from './design/live-control/admin/IntegrationsCatalogPage.js'
import { ServiceIntegrationPage } from './design/live-control/admin/ServiceIntegrationPage.js'
import { SecurityPage } from './design/live-control/admin/SecurityPage.js'
import { GeneralPage } from './design/live-control/admin/GeneralPage.js'
import { AppearancePage } from './design/live-control/admin/AppearancePage.js'
import { ProfilePage } from './design/live-control/ProfilePage.js'
import { SecurityHub } from './design/live-control/hubs/SecurityHub.js'
import { SoireesPage } from './design/live-control/soirees/SoireesPage.js'
import { ComparePage } from './design/live-control/soirees/ComparePage.js'
import { MarketPage } from './design/live-control/soirees/MarketPage.js'
import { SoireesSettingsPage } from './design/live-control/soirees/SoireesSettingsPage.js'
import { SoireesSettingsHub } from './design/live-control/soirees/SoireesSettingsHub.js'
import { RequirePermission } from './design/live-control/auth/RequirePermission.js'
import { FamiliesPage } from './design/live-control/soirees/FamiliesPage.js'
import { CalendarPage } from './design/live-control/calendar/CalendarPage.js'
import { SoireePage } from './design/live-control/soirees/SoireePage.js'
import { ParcHub } from './design/live-control/hubs/OtherHubs.js'
import { CommunicationHomePage } from './design/live-control/communication/CommunicationHome.js'
import {
  CommInstagramIntegrationPage, CommGoogleIntegrationPage,
} from './design/live-control/communication/IntegrationsPages.js'
import { CommReviewsPage, CommReviewDetailPage } from './design/live-control/communication/ReviewsPages.js'
import {
  CommPublicationsPage, CommComposerPage,
} from './design/live-control/communication/PublicationsPages.js'
import { InstagramAnalyticsPage } from './design/live-control/communication/InstagramPage.js'
import { IgContentListPage, IgContentDetailPage } from './design/live-control/communication/InstagramContentPages.js'
import { IgPublicationsPage, IgReelsPage, IgStoriesPage, IgCommentairesPage, IgAudiencePage } from './design/live-control/communication/InstagramModulePages.js'
import { GoogleHubPage } from './design/live-control/communication/GoogleHubPage.js'
import { AcquisitionLinksPage, AcquisitionLinkDetailPage, AcquisitionLinkNewPage, AcquisitionLinkEditPage } from './design/live-control/communication/AcquisitionPages.js'
import { CommTrackerIntegrationPage } from './design/live-control/communication/TrackerIntegrationPage.js'
import { InfraSourcePage } from './design/live-control/hubs/InfraPages.js'
import {
  FinanceHome, FinanceClosuresPage, FinanceClosingWizardPage, FinanceRevenuePage,
  FinanceAnnualPage, FinanceBankPage, FinanceReferencesPage, FinanceRulesPage,
  FinanceSourcesPage, FinanceSourceDetailPage, FinanceImportPage,
  FinanceNightsCalendarPage, FinanceNightPage,
  FinanceSalesPage,
} from './design/live-control/finance/FinancePages.js'
import { ZapIntegrationPage } from './design/live-control/admin/ZapIntegrationPage.js'
import { FamilyHubPage } from './design/live-control/navigation/FamilyHubPage.js'
import { ModuleShellPage } from './design/live-control/navigation/ModuleShellPage.js'
import {
  AccessOverviewPage, AccessPeoplePage, AccessPersonPage, AccessVisitorsPage,
  AccessCredentialsPage, AccessCredentialPage, AccessDoorsPage, AccessDoorPage, AccessPoliciesPage,
  AccessPolicyPage, AccessSchedulesPage, AccessIntercomPage, AccessHistoryPage,
  AccessDevicesPage, AccessEmergencyPage, AccessGroupsPage,
} from './design/live-control/access/AccessModule.js'
import { UserPermissionsPage, RolePermissionsPage, PreviewAsUserPage } from './design/live-control/admin/PermissionsPages.js'
import { AccessIntegrationPage } from './design/live-control/admin/AccessIntegrationPage.js'
import { InfraIntegrationPage } from './design/live-control/admin/InfraIntegrationPage.js'
import { ShotgunIntegrationPage } from './design/live-control/admin/ShotgunIntegrationPage.js'
import { PretixIntegrationPage } from './design/live-control/admin/PretixIntegrationPage.js'
import { BilletteriesSettingsPage } from './design/live-control/soirees/BilletteriesSettingsPage.js'
import { InfrastructureHub, AllServicesPage, InfraServicePage } from './design/live-control/hubs/InfraCatalog.js'
import { SignalementsListePage } from './design/live-control/technique/SignalementsListePage.js'
import { SignalementNouveauPage } from './design/live-control/technique/SignalementNouveauPage.js'
import { SignalementFichePage } from './design/live-control/technique/SignalementFichePage.js'
import { AssociationsListePage } from './design/live-control/associations/AssociationsListePage.js'
import { redirectionADecider } from './design/live-control/soirees/rattachement-model.js'
import { usePermissions } from './design/live-control/admin/primitives.js'
import { AssociationFichePage } from './design/live-control/associations/AssociationFichePage.js'
import { ComparerAssociationsPage } from './design/live-control/associations/ComparerAssociationsPage.js'
import { AssociationsSettingsPage } from './design/live-control/associations/AssociationsSettingsPage.js'
// auth.css APRÈS les modules : ses variantes (.lc-btn--danger) doivent
// pouvoir surcharger components.css à spécificité égale.
import './design/live-control/auth.css'
import './design/live-control/porte/porte.css'
import { LoginPage } from './design/live-control/auth/Login.js'
import { MfaPage } from './design/live-control/auth/Mfa.js'
import { MfaSetupPage } from './design/live-control/auth/MfaSetup.js'
import { NewPasswordPage } from './design/live-control/auth/NewPassword.js'
import { AccessDeniedPage, SessionExpiredPage } from './design/live-control/auth/InfoPages.js'
import { DialogShowcase } from './design/live-control/auth/DialogShowcase.js'

import { PointeuseKiosk } from './design/live-control/pointeuse/PointeuseKiosk.js'
import { PorteConnexionPage } from './design/live-control/porte/PorteConnexionPage.js'

function AuthWorld({ path }: { path: string }) {
  switch (path) {
    case '/login': return <LoginPage />
    case '/login/mfa': return <MfaPage />
    case '/login/mfa-setup': return <MfaSetupPage />
    case '/login/new-password': return <NewPasswordPage />
    case '/session-expired': return <SessionExpiredPage />
    case '/access-denied': return <AccessDeniedPage />
    default: return <DialogShowcase />
  }
}

function RouteContent({ path }: { path: string }) {
  /* Une page dont la barre latérale ouvre des VUES par l'adresse (`?vue=…`,
     sous-familles de la Comptabilité) doit relire son adresse à CHAQUE
     navigation, même vers son propre chemin : cliquer « Écarts » depuis la
     liste des clôtures ne change pas le chemin, la page restait montée sur sa
     vue précédente sous une adresse qui en annonçait une autre. La clé de
     navigation la remonte ; le simple réglage d'un filtre (`remplacerRecherche`)
     n'est pas une navigation et ne la remonte jamais. */
  const navigations = useNavigationCount()
  // Route dynamique : /settings/users/:id → fiche utilisateur pleine page.
  const userDetail = /^\/settings\/users\/([0-9a-f-]{8,40})$/.exec(path)
  const personDetail = /^\/equipe\/rh\/salaries\/([0-9a-f-]{8,40})$/.exec(path)
  // Lot 4.6 : détail compteurs d'un salarié = vraie page (pas une modale).
  // Lisible par l'équipe OU pour soi-même : l'API accepte les deux droits,
  // la page affiche « Hors de votre périmètre » si le serveur refuse.
  const counterDetail = /^\/equipe\/temps\/compteurs\/([0-9a-f-]{8,40})$/.exec(path)
  if (counterDetail) return <RequirePermission perm={['time.account.read.team', 'time.account.read.self']}><HrCountersPersonPage personId={counterDetail[1]!} /></RequirePermission>

  // ─── SIRH UI CONSOLIDATION : /rh-access/* n'existe plus qu'en REDIRECT.
  //     Une seule implémentation par surface — jamais deux (§6).
  const legacyPerson = /^\/rh-access\/people\/([0-9a-f-]{8,40})$/.exec(path)
  if (legacyPerson) return <Redirection to={`/equipe/rh/salaries/${legacyPerson[1]}`} />
  if (path === '/rh-access') return <Redirection to="/equipe/rh" />
  if (path === '/rh-access/people') return <Redirection to="/equipe/rh/salaries" />
  if (path === '/rh-access/presence') return <Redirection to="/settings/integrations/skello" />
  // PRODUCT RESET (Lot 2.5, §8/§41) : l'ancienne Présence Skello quitte le
  // module Temps — elle vit en diagnostic dans l'intégration Skello.
  if (path === '/equipe/temps/presences') return <Redirection to="/settings/integrations/skello" />
  if (path === '/rh-access/reconciliation') return <Redirection to="/settings/integrations/skello" />
  if (path === '/rh-access/sync') return <Redirection to="/settings/integrations/skello" />

  const teamDetail = /^\/equipe\/organisation\/equipes\/([0-9a-f-]{8,40})$/.exec(path)
  if (teamDetail) return <RequirePermission perm="hr.org.read"><HrTeamDetailPage id={teamDetail[1]!} /></RequirePermission>
  const posteDetail = /^\/equipe\/organisation\/postes\/([0-9a-f-]{8,40})$/.exec(path)
  if (posteDetail) return <RequirePermission perm="hr.org.read"><HrPositionDetailPage id={posteDetail[1]!} /></RequirePermission>
  const fonctionDetail = /^\/equipe\/organisation\/fonctions\/([0-9a-f-]{8,40})$/.exec(path)
  if (fonctionDetail) return <RequirePermission perm="hr.org.read"><HrFunctionDetailPage id={fonctionDetail[1]!} /></RequirePermission>

  // ─── Contrôle d'accès V1 (module Sécurité) ───
  const acPerson = /^\/securite\/controle-acces\/personnes\/([0-9a-f-]{8,40})$/.exec(path)
  if (acPerson) return <RequirePermission perm="access.view"><AccessPersonPage id={acPerson[1]!} /></RequirePermission>
  const acDoor = /^\/securite\/controle-acces\/portes\/([0-9a-f-]{8,40})$/.exec(path)
  if (acDoor) return <RequirePermission perm="access.doors.view"><AccessDoorPage id={acDoor[1]!} /></RequirePermission>
  const acPolicy = /^\/securite\/controle-acces\/regles\/([0-9a-f-]{8,40})$/.exec(path)
  if (acPolicy) return <RequirePermission perm="access.policies.view"><AccessPolicyPage id={acPolicy[1]!} /></RequirePermission>
  if (path === '/securite/controle-acces') return <RequirePermission perm="access.view"><AccessOverviewPage /></RequirePermission>
  if (path === '/securite/controle-acces/personnes') return <RequirePermission perm="access.view"><AccessPeoplePage /></RequirePermission>
  if (path === '/securite/controle-acces/groupes') return <RequirePermission perm="access.view"><AccessGroupsPage /></RequirePermission>
  if (path === '/securite/controle-acces/visiteurs') return <RequirePermission perm="access.visitors.view"><AccessVisitorsPage /></RequirePermission>
  if (path === '/securite/controle-acces/identifiants') return <RequirePermission perm="access.credentials.view"><AccessCredentialsPage /></RequirePermission>
  const acCred = /^\/securite\/controle-acces\/identifiants\/([\w:.%-]+)$/.exec(path)
  if (acCred) return <RequirePermission perm="access.credentials.view"><AccessCredentialPage id={decodeURIComponent(acCred[1]!)} /></RequirePermission>
  if (path === '/securite/controle-acces/portes') return <RequirePermission perm="access.doors.view"><AccessDoorsPage /></RequirePermission>
  if (path === '/securite/controle-acces/regles') return <RequirePermission perm="access.policies.view"><AccessPoliciesPage /></RequirePermission>
  if (path === '/securite/controle-acces/horaires') return <RequirePermission perm="access.policies.view"><AccessSchedulesPage /></RequirePermission>
  if (path === '/securite/controle-acces/interphonie') return <RequirePermission perm="access.intercom.view"><AccessIntercomPage /></RequirePermission>
  if (path === '/securite/controle-acces/historique') return <RequirePermission perm="access.history.view"><AccessHistoryPage /></RequirePermission>
  if (path === '/securite/controle-acces/appareils') return <RequirePermission perm="access.devices.view"><AccessDevicesPage /></RequirePermission>
  if (path === '/securite/controle-acces/urgence') return <RequirePermission perm="access.view"><AccessEmergencyPage /></RequirePermission>
  if (path === '/settings/integrations/service-credentials') return <RequirePermission perm="integration.read"><ServiceCredentialsPage /></RequirePermission>
  if (path === '/settings/integrations/unifi-access') return <RequirePermission perm="integration.read"><AccessIntegrationPage /></RequirePermission>
  // Pages des intégrations à clé d'accès (15/10/2026) : leurs cartes du
  // catalogue menaient au catalogue lui-même.
  if (path === '/settings/integrations/unifi-network') return <RequirePermission perm="integration.read"><InfraIntegrationPage integration="unifi_network" /></RequirePermission>
  if (path === '/settings/integrations/unifi-protect') return <RequirePermission perm="integration.read"><InfraIntegrationPage integration="unifi_protect" /></RequirePermission>
  if (path === '/settings/integrations/home-assistant') return <RequirePermission perm="integration.read"><InfraIntegrationPage integration="home_assistant" /></RequirePermission>
  // SUPERVISION (21/10/2026) — Sonde : le raccordement se règle dans
  // Intégrations, la vue d'ensemble vit dans Paramètres.
  if (path === '/settings/integrations/sonde') return <RequirePermission perm="integration.read"><SondeIntegrationPage /></RequirePermission>
  if (path === '/settings/supervision') return <RequirePermission perm={['sonde.read', 'integration.read']}><SupervisionOverviewPage /></RequirePermission>
  // Réglages de Sonde, générés depuis son schéma (21/10/2026) : Journal et
  // Consommation AVANT le motif des groupes, dont le code est validé ici.
  if (path === '/settings/supervision/journal') return <RequirePermission perm="sonde.read"><SupervisionJournalPage /></RequirePermission>
  if (path === '/settings/supervision/consommation') return <RequirePermission perm="sonde.read"><SupervisionConsommationPage /></RequirePermission>
  const supGroupe = /^\/settings\/supervision\/([a-z][a-z0-9_]{1,40})$/.exec(path)
  if (supGroupe) return <RequirePermission perm="sonde.read"><SupervisionGroupePage groupe={supGroupe[1]!} key={supGroupe[1]!} /></RequirePermission>
  // INTERPHONE IA (22/10/2026) — raccordement et réglages de Sacha (service Interphone).
  if (path === '/settings/integrations/billetterie') return <RequirePermission perm="integration.read"><BilletterieIntegrationPage /></RequirePermission>
  // Réglages › Billetterie (22/10/2026) : les soirées de Billetterie Live Club depuis Gestion — lisible avec
  // « Voir », modifiable avec « Régler » (le serveur tranche, confirmation d'identité comprise).
  if (path === '/settings/billetterie') return <RequirePermission perm="ticketing.event.read"><ReglagesBilletteriePage /></RequirePermission>
  if (path === '/settings/integrations/interphone') return <RequirePermission perm={['integration.read', 'interphone.credentials.manage']}><InterphoneIntegrationPage /></RequirePermission>
  if (path === '/settings/integrations/tablettes') return <RequirePermission perm={['integration.read', 'tablettes.enroll']}><TablettesIntegrationPage /></RequirePermission>
  if (path === '/settings/interphone') return <RequirePermission perm="interphone.read"><InterphoneReglagesPage /></RequirePermission>
  if (path === '/settings/integrations/shotgun') return <RequirePermission perm="integration.read"><ShotgunIntegrationPage /></RequirePermission>
  if (path === '/settings/integrations/pretix') return <RequirePermission perm="integration.read"><PretixIntegrationPage /></RequirePermission>
  // La relève des rapports ZAP : la notification « Relève en échec » y mène ses
  // destinataires (titulaires de `finance.source.email.connect`), qui n'ont pas
  // forcément `integration.read`. Le serveur tranche chaque geste (lecture
  // `finance.source.read`, connexion `finance.source.email.connect` + step-up).
  if (path === '/settings/integrations/zap-sis') return <RequirePermission perm={['integration.read', 'finance.source.read', 'finance.source.email.connect']}><ZapIntegrationPage /></RequirePermission>

  // ─── Permission System V2 ───
  const userPerms = /^\/settings\/users\/([0-9a-f-]{8,40})\/permissions$/.exec(path)
  if (userPerms) return <RequirePermission perm="user.read"><UserPermissionsPage id={userPerms[1]!} /></RequirePermission>
  const userPreview = /^\/settings\/users\/([0-9a-f-]{8,40})\/preview$/.exec(path)
  if (userPreview) return <RequirePermission perm="user.read"><PreviewAsUserPage id={userPreview[1]!} /></RequirePermission>
  const rolePerms = /^\/settings\/roles\/([a-z_]+)\/permissions$/.exec(path)
  if (rolePerms) return <RequirePermission perm="role.read"><RolePermissionsPage roleKey={rolePerms[1]!} /></RequirePermission>
  const infraService = /^\/infrastructure\/services\/([a-z0-9-]+)$/.exec(path)
  if (infraService) return <InfraServicePage slug={infraService[1]!} />
  // Soirées — cockpit opérationnel de la nuit.
  if (path.startsWith('/soirees/compare')) {
    const qp = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '')
    return <ComparePage a={qp.get('a')} b={qp.get('b')} />
  }
  if (path === '/soirees/market') return <MarketPage />
  /* Réglages Soirées : un hub, puis une page par domaine. `/familles` garde sa
     page dédiée, déjà écrite comme telle.

     La garde est ICI, une fois, à l'entrée de la route — et nulle part
     ailleurs. C'est la seule chose autorisée à conclure « accès refusé » : un
     appel API refusé en cours de page ne détourne plus le document. */
  if (path === '/settings/soirees') {
    return <RequirePermission perm="soiree.read"><SoireesSettingsHub /></RequirePermission>
  }
  if (path === '/settings/soirees/familles') {
    return <RequirePermission perm="soiree.read"><FamiliesPage /></RequirePermission>
  }
  if (path === '/settings/soirees/billetteries') {
    return <RequirePermission perm="soiree.read"><BilletteriesSettingsPage /></RequirePermission>
  }
  // Réglages Soirées › Associations : les droits DÉDIÉS des associations, pas ceux des soirées.
  if (path === '/settings/soirees/associations') {
    return <RequirePermission perm="association.read"><AssociationsSettingsPage /></RequirePermission>
  }
  const sectionSoirees = /^\/settings\/soirees\/(saisons|salles|veille|instagram|recherche-web|previsions|caisse)$/
    .exec(path)?.[1]
  if (sectionSoirees) {
    return (
      <RequirePermission perm="soiree.read">
        <SoireesSettingsPage section={sectionSoirees as never} />
      </RequirePermission>
    )
  }
  if (path === '/calendrier') return <CalendarPage />
  /* ─── EXPLOITATION › SOIRÉES › BILLETTERIE LIVE CLUB (22/10/2026) ───
     Les soirées de la billetterie maison, lues et écrites par l'API de Gestion
     (contrat soirees-v1). Rien n'entre dans les tables de Gestion : ce sont
     d'autres soirées que celles de Shotgun. Déclarées AVANT la fiche Shotgun,
     dont le motif ne capte de toute façon ni « nouvelle » ni « billetterie ».
     L'assistant lit aussi la liste et les salles : il demande les deux droits.
     Depuis le 22/10 (§9.6), c'est une FENÊTRE par-dessus la page Soirées :
     `/soirees/nouvelle` montre la page avec la fenêtre ouverte ; la refermer
     rend `/soirees`. */
  if (path === '/soirees/nouvelle') return <RequirePermission perm="ticketing.event.read"><RequirePermission perm="ticketing.event.prepare"><SoireesPage nouvelle /></RequirePermission></RequirePermission>
  if (path === '/soirees/billetterie') return <RequirePermission perm="ticketing.event.read"><SoireesMaisonPage key={navigations} /></RequirePermission>
  const soireeMaison = /^\/soirees\/billetterie\/([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})$/.exec(path)
  if (soireeMaison) return <RequirePermission perm="ticketing.event.read"><SoireeMaisonPage id={soireeMaison[1]!.toLowerCase()} key={soireeMaison[1]!.toLowerCase()} /></RequirePermission>
  const soireeDetail = /^\/soirees\/([0-9a-f-]{8,40})$/.exec(path)
  if (soireeDetail) return <SoireePage id={soireeDetail[1]!} />
  if (path === '/soirees') return <SoireesPage />
  // LOT 9.1 — handoff d'authentification mobile (navigateur système).
  if (path === '/mobile/auth/start') return <MobileAuthStartPage />
  if (path === '/mobile/auth/callback') return <MobileAuthCallbackPage />
  /*
   * CENTRE D'ACTIVITÉ (Phase 2) — une seule destination.
   *
   * `/alerts`, `/alerts/:id` et `/notifications` ne sont pas supprimées :
   * elles REDIRIGENT. Les liens profonds déjà envoyés par notification
   * pointent vers `/alerts/<uuid>` et doivent continuer d'ouvrir l'incident.
   * La redirection remplace l'entrée d'historique — le bouton Retour ne
   * repasse donc jamais par l'ancienne adresse.
   *
   * `/alerts/rules` reste où elle est : c'est un écran de CONFIGURATION, pas
   * le centre. Elle est testée avant les autres motifs `/alerts/…`.
   */
  /* COMMUNICATION V1 — le cockpit remplace l'ancien hub vitrine. De vraies
     pages partout, AUCUN drawer dans le module (doctrine du lot). */
  /* V1.1 — analytics Instagram propriétaire : vraies pages, aucun drawer. */
  if (path === '/communication/instagram') return <InstagramAnalyticsPage />
  // V2.1 — fonctions du module Instagram (mêmes données, type verrouillé).
  if (path === '/communication/instagram/publications') return <IgPublicationsPage />
  if (path === '/communication/instagram/reels') return <IgReelsPage />
  if (path === '/communication/instagram/stories') return <IgStoriesPage />
  if (path === '/communication/instagram/commentaires') return <IgCommentairesPage />
  if (path === '/communication/instagram/audience') return <IgAudiencePage />
  if (path === '/communication/google') return <GoogleHubPage />
  if (path === '/communication/instagram/contenus') return <IgContentListPage />
  const igContenu = /^\/communication\/instagram\/contenus\/([0-9a-f-]{8,40})$/.exec(path)
  if (igContenu) return <IgContentDetailPage id={igContenu[1]!} />
  // ACQUISITION CLUB V1 — le panneau de contrôle du tracker. Vraies pages.
  if (path === '/settings/integrations/main-courante') return <ServiceIntegrationPage serviceKey="main-courante" />
  if (path === '/settings/integrations/parc') return <ServiceIntegrationPage serviceKey="parc" />
  if (path === '/communication/liens/nouveau') return <AcquisitionLinkNewPage />
  const lienEdit = /^\/communication\/liens\/([a-z0-9-]{1,40})\/modifier$/.exec(path)
  if (lienEdit) return <AcquisitionLinkEditPage slug={lienEdit[1]!} />
  const lienDetail = /^\/communication\/liens\/([a-z0-9-]{1,40})$/.exec(path)
  if (lienDetail) return <AcquisitionLinkDetailPage slug={lienDetail[1]!} />
  if (path === '/communication/liens') return <AcquisitionLinksPage />
  if (path === '/communication/integrations/tracker') return <CommTrackerIntegrationPage />
  if (path === '/communication/integrations/instagram') return <CommInstagramIntegrationPage />
  if (path === '/communication/integrations/google') return <CommGoogleIntegrationPage />
  // V2.1 : les intégrations n'ont plus qu'UN domicile — Administration.
  // L'ancienne adresse reste vivante pour les favoris : redirection.
  if (path === '/communication/integrations') return <Redirection to="/settings/integrations" />
  const avisDetail = /^\/communication\/avis\/(.{4,200})$/.exec(path)
  if (avisDetail) return <CommReviewDetailPage id={decodeURIComponent(avisDetail[1]!)} />
  if (path === '/communication/avis') return <CommReviewsPage />
  if (path === '/communication/publications/nouvelle') return <CommComposerPage id={null} />
  const pubDetail = /^\/communication\/publications\/([0-9a-f-]{8,40})$/.exec(path)
  if (pubDetail) return <CommComposerPage id={pubDetail[1]!} />
  if (path === '/communication/publications') return <CommPublicationsPage />
  // Ancienne adresse V1 : redirige vers la page contenu Instagram.
  const contenuDetail = /^\/communication\/contenus\/([0-9a-f-]{8,40})$/.exec(path)
  if (contenuDetail) return <Redirection to={`/communication/instagram/contenus/${contenuDetail[1]!}`} />

  /* ─── COMPTABILITÉ (7ᵉ famille, 04/09/2026) ───
     La garde de permission est ICI, une fois, à l'entrée de la route : §85
     et §187 exigent que sans le droit, AUCUN euro ne parte au navigateur —
     on ne masque pas un composant après coup, on ne charge pas la page.
     `/comptabilite/clotures/:id` est l'ASSISTANT DE CLÔTURE : une vraie
     route persistante (§88), rechargeable et reprenable — jamais un état
     local dans un Drawer. */
  // Une clôture s'adresse par sa NUIT (« 2026-09-26-the-culture-… ») ou, pour
  // les anciens liens et les révisions remplacées, par son identifiant.
  const clotureDetail = /^\/comptabilite\/clotures\/([0-9a-z-]{8,120})(?:\/[a-z0-9-]{1,60})?$/.exec(path)
  if (clotureDetail) return <RequirePermission perm="finance.closure.read"><FinanceClosingWizardPage reference={clotureDetail[1]!} key={clotureDetail[1]!} /></RequirePermission>
  if (path === '/comptabilite') return <RequirePermission perm="finance.dashboard.read"><FinanceHome /></RequirePermission>
  if (path === '/comptabilite/clotures') return <RequirePermission perm="finance.closure.read"><FinanceClosuresPage key={navigations} /></RequirePermission>
  if (path === '/comptabilite/chiffre-affaires') return <RequirePermission perm="finance.revenue.read"><FinanceRevenuePage /></RequirePermission>
  if (path === '/comptabilite/chiffre-affaires/annuel') return <RequirePermission perm="finance.revenue.read"><FinanceAnnualPage key={navigations} /></RequirePermission>
  if (path === '/comptabilite/banque') return <RequirePermission perm="finance.bank.read"><FinanceBankPage key={navigations} /></RequirePermission>
  if (path === '/comptabilite/referentiels') return <RequirePermission perm="finance.reference.read"><FinanceReferencesPage /></RequirePermission>
  if (path === '/comptabilite/regles') return <RequirePermission perm="finance.reference.read"><FinanceRulesPage /></RequirePermission>
  /* Lot 1.1 — les sources reçues. Le détail est une VRAIE page (pas un tiroir) :
     on y arrive depuis une notification, on la partage, on la recharge. */
  const sourceDetail = /^\/comptabilite\/sources\/([0-9a-f-]{8,40})$/.exec(path)
  if (sourceDetail) return <RequirePermission perm="finance.source.read"><FinanceSourceDetailPage id={sourceDetail[1]!} /></RequirePermission>
  if (path === '/comptabilite/sources') return <RequirePermission perm="finance.source.read"><FinanceSourcesPage /></RequirePermission>
  if (path === '/comptabilite/import') return <RequirePermission perm="finance.history.import"><FinanceImportPage /></RequirePermission>
  /* Les nuits — le calendrier et la fiche d'une nuit, de vraies routes. */
  const nuitDetail = /^\/comptabilite\/nuits\/(\d{4}-\d{2}-\d{2})$/.exec(path)
  if (nuitDetail) return <RequirePermission perm="finance.closure.read"><FinanceNightPage date={nuitDetail[1]!} /></RequirePermission>
  if (path === '/comptabilite/nuits') return <RequirePermission perm="finance.closure.read"><FinanceNightsCalendarPage key={navigations} /></RequirePermission>
  // Ventes & produits : la garde est celle de l'API (`finance.revenue.read`) ; les offerts
  // y suivent `finance.offers.read`, décidé par le serveur.
  if (path === '/comptabilite/ventes') return <RequirePermission perm="finance.revenue.read"><FinanceSalesPage /></RequirePermission>

  /* ─── TECHNIQUE — SIGNALEMENTS (09/10/2026) ───
     Technique est la source de vérité ; ces écrans passent par l'API de
     GESTION, qui relaie. La garde est ICI, à l'entrée de chaque route : sans
     `technique.signalements.read`, aucun écran ne se charge ; la création
     demande `create` EN PLUS (son formulaire lit le catalogue `/meta`, que
     le serveur ouvre au droit `read`). « nouveau » est testé avant la fiche,
     dont l'adresse est une référence `SIG-AAAA-NNNN` ou un identifiant
     numérique. La liste relit son adresse à chaque navigation (`key`) : un
     Retour depuis une fiche la retrouve avec ses filtres. */
  if (path === '/technique/signalements/nouveau') return <RequirePermission perm="technique.signalements.read"><RequirePermission perm="technique.signalements.create"><SignalementNouveauPage /></RequirePermission></RequirePermission>
  const signalement = /^\/technique\/signalements\/(SIG-\d{4}-\d{4,6}|\d{1,10})$/.exec(path)
  if (signalement) return <RequirePermission perm="technique.signalements.read"><SignalementFichePage reference={signalement[1]!} key={signalement[1]!} /></RequirePermission>
  if (path === '/technique/signalements') return <RequirePermission perm="technique.signalements.read"><SignalementsListePage key={navigations} /></RequirePermission>

  /* ─── EXPLOITATION › ASSOCIATIONS (20/10/2026) ───
     Droits DÉDIÉS (« Consulter les associations ») : la note et le rôle du
     contact ne voyagent pas avec « Consulter les soirées ». La garde est ICI,
     à l'entrée de chaque route. « comparer » est testé avant la fiche (son
     motif n'accepte qu'un identifiant). La liste relit son adresse à chaque
     navigation (`key`) : un Retour depuis une fiche la retrouve avec ses
     filtres, et un lien « À décider » reçu ailleurs défile jusqu'au bloc. */
  if (path === '/exploitation/associations/comparer') return <RequirePermission perm="association.read"><ComparerAssociationsPage key={navigations} /></RequirePermission>
  const association = /^\/exploitation\/associations\/([0-9a-f-]{36})$/.exec(path)
  if (association) return <RequirePermission perm="association.read"><AssociationFichePage id={association[1]!} key={association[1]!} /></RequirePermission>
  if (path === '/exploitation/associations') return <RouteListeAssociations navigations={navigations} />

  /* ─── SÉCURITÉ › INTERPHONE IA (22/10/2026) ─── Le journal et les fiches
     sont lus en direct chez le service Interphone. L'identifiant d'appel est
     validé par son motif (celui du service). */
  if (path === '/securite/interphone-ia') return <RequirePermission perm="interphone.read"><InterphonePage /></RequirePermission>
  const appelInterphone = /^\/securite\/interphone-ia\/appels\/([A-Za-z0-9_-]{1,80})$/.exec(path)
  if (appelInterphone) return <RequirePermission perm="interphone.read"><InterphoneAppelPage id={appelInterphone[1]!} key={appelInterphone[1]!} /></RequirePermission>

  /* IA V2 — hubs de famille + shells de modules non configurés. Les
     routes historiques ne bougent pas : la taxonomie est visuelle. */
  if (path === '/exploitation') return <FamilyHubPage familyId="exploitation" />
  if (path === '/equipe') return <FamilyHubPage familyId="equipe" />
  if (path === '/securite') return <FamilyHubPage familyId="securite" />
  if (path === '/technique') return <FamilyHubPage familyId="technique" />
  if (path === '/administration') return <FamilyHubPage familyId="administration" />
  if (path === '/securite/controle-acces') return <ModuleShellPage moduleId="controle-acces" />
  // Écrans de la supervision technique, lus dans Sonde par le relais (21/10/2026).
  if (path === '/securite/videosurveillance') return <RequirePermission perm="sonde.read"><VideoPage /></RequirePermission>
  if (path === '/technique/reseau') return <RequirePermission perm="sonde.read"><ReseauPage /></RequirePermission>
  if (path === '/technique/batiment') return <RequirePermission perm="sonde.read"><BatimentPage /></RequirePermission>
  if (path === '/technique/batiment/onduleurs') return <RequirePermission perm="sonde.read"><OnduleursPage /></RequirePermission>
  if (path === '/technique/audiovisuel') return <RequirePermission perm="sonde.read"><AudiovisuelPage /></RequirePermission>
  // TABLETTES MURALES (02/11/2026) — lues et pilotées via kiosk-api (relais /api/v1/tablettes).
  if (path === '/technique/tablettes') return <RequirePermission perm="tablettes.read"><TablettesPage /></RequirePermission>
  if (path === '/technique/tablettes/enroler') return <RequirePermission perm="tablettes.enroll"><EnrolementPage /></RequirePermission>
  const tablette = /^\/technique\/tablettes\/([A-Za-z0-9][A-Za-z0-9_-]{0,63})$/.exec(path)
  if (tablette) return <RequirePermission perm="tablettes.read"><TablettePage id={tablette[1]!} key={tablette[1]!} /></RequirePermission>

  if (path === '/activite') return <ActivityCenterPage />
  const activiteDetail = /^\/activite\/([0-9a-f-]{8,40})$/.exec(path)
  if (activiteDetail) return <ActivityCenterPage focusInstance={activiteDetail[1]!} />

  // LOT 9 — règles d'alerte, incidents (deep links), modèles de message.
  if (path === '/alerts/rules') return <RulesPage />
  if (path === '/alerts/rules/new') return <RuleBuilderPage ruleId={null} />
  const ruleEdit = /^\/alerts\/rules\/([0-9a-f-]{8,40})$/.exec(path)
  if (ruleEdit) return <RuleBuilderPage ruleId={ruleEdit[1]!} />
  const alertInstance = /^\/alerts\/([0-9a-f-]{8,40})$/.exec(path)
  if (alertInstance) return <Redirection to={`/activite/${alertInstance[1]!}`} />
  if (path === '/notifications') return <Redirection to="/activite" />
  const tplEdit = /^\/settings\/communications\/templates\/([0-9a-f-]{8,40})$/.exec(path)
  if (tplEdit) return <TemplateEditorPage templateId={tplEdit[1]!} />
  if (path === '/settings/communications') return <CommunicationsPage />
  if (path === '/infrastructure/services') return <AllServicesPage />
  /* SUPERVISION (Sonde, 21/10/2026) — déclarées AVANT le motif des sources
     ci-dessous, qui prendrait sinon « live », « nuits », « serveur »… pour un
     slug de source. Les adresses produites par Sonde (/infrastructure,
     /infrastructure/appareils/<uid>, /incidents/<id>, /salles/<code>) sont
     honorées telles quelles ; chaque paramètre est validé par son motif. */
  if (path === '/infrastructure/live') return <RequirePermission perm="sonde.read"><ModeSoireePage /></RequirePermission>
  if (path === '/infrastructure/incidents') return <RequirePermission perm="sonde.read"><ListeIncidentsPage /></RequirePermission>
  const svIncident = /^\/infrastructure\/incidents\/([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})$/.exec(path)
  if (svIncident) return <RequirePermission perm="sonde.read"><FicheIncidentPage id={svIncident[1]!.toLowerCase()} key={svIncident[1]!} /></RequirePermission>
  const svAppareil = /^\/infrastructure\/appareils\/([a-z0-9][a-z0-9-]{1,62})$/.exec(path)
  if (svAppareil) return <RequirePermission perm="sonde.read"><FicheAppareilPage uid={svAppareil[1]!} key={svAppareil[1]!} /></RequirePermission>
  const svSalle = /^\/infrastructure\/salles\/([A-Za-z0-9][A-Za-z0-9-]{0,31})$/.exec(path)
  if (svSalle) return <RequirePermission perm="sonde.read"><FicheSallePage code={svSalle[1]!} key={svSalle[1]!} /></RequirePermission>
  if (path === '/infrastructure/nuits') return <RequirePermission perm="sonde.read"><NuitsPage /></RequirePermission>
  const svNuit = /^\/infrastructure\/nuits\/(\d{4}-\d{2}-\d{2})$/.exec(path)
  if (svNuit) return <RequirePermission perm="sonde.read"><NuitPage date={svNuit[1]!} key={svNuit[1]!} /></RequirePermission>
  if (path === '/infrastructure/serveur') return <RequirePermission perm="sonde.read"><ServeurPage /></RequirePermission>
  if (path === '/infrastructure/registre') return <RequirePermission perm="sonde.details.read"><RegistrePage /></RequirePermission>
  // Sans code ni identifiant, une salle ou un appareil n'a pas d'écran : le
  // hub les montre tous — jamais une « source » nommée salles ou appareils.
  if (path === '/infrastructure/salles' || path === '/infrastructure/appareils') return <Redirection to="/infrastructure" />
  const infraDetail = /^\/infrastructure\/(?!services)([a-z][a-z-]+)$/.exec(path)
  if (infraDetail) return <InfraSourcePage slug={infraDetail[1]!} />
  if (personDetail) return <RhPersonPage id={personDetail[1]!} />
  if (userDetail) return <UserDetailPage userId={userDetail[1]!} />

  switch (path) {
    case '/equipe/organisation': return <RequirePermission perm="hr.org.read"><HrOrgHubPage /></RequirePermission>
    case '/equipe/organisation/postes': return <RequirePermission perm="hr.org.read"><HrPositionsPage /></RequirePermission>
    case '/equipe/organisation/fonctions': return <RequirePermission perm="hr.org.read"><HrFunctionsPage /></RequirePermission>
    case '/equipe/organisation/lieux': return <RequirePermission perm="hr.org.read"><HrLocationsPage /></RequirePermission>
    case '/equipe/temps': return <RequirePermission perm="time.schedule.read"><HrTempsHomePage /></RequirePermission>
    case '/equipe/organisation/equipes': return <RequirePermission perm="hr.org.read"><HrTeamsPage /></RequirePermission>
    case '/equipe/organisation/competences': return <RequirePermission perm="hr.org.read"><HrSkillsPage /></RequirePermission>
    case '/equipe/organisation/organigramme': return <RequirePermission perm="hr.org.read"><HrOrgChartPage /></RequirePermission>
    case '/equipe/temps/planning': return <RequirePermission perm="time.schedule.read"><PlanningPage key={navigations} /></RequirePermission>
    case '/equipe/temps/validation': return <RequirePermission perm="time.approve"><HrValidationWorkstation /></RequirePermission>
    case '/equipe/temps/pointeuse': return <RequirePermission perm="time.clock.admin"><HrPointeuseAdminPage /></RequirePermission>
    case '/equipe/temps/presence': return <RequirePermission perm="time.read.team"><HrPresencePage /></RequirePermission>
    case '/equipe/temps/signatures': return <RequirePermission perm="time.signature.read"><HrSignaturesPage /></RequirePermission>
    case '/equipe/temps/annonces': return <RequirePermission perm="hr.announcement.read"><HrAnnoncesPage /></RequirePermission>
    case '/equipe/temps/conges': return <RequirePermission perm="leave.approve"><HrCongesPage /></RequirePermission>
    case '/equipe/temps/disponibilites': return <RequirePermission perm="availability.manage"><HrDisposPage /></RequirePermission>
    case '/equipe/temps/compteurs': return <RequirePermission perm="time.account.read.team"><HrCountersHomePage /></RequirePermission>
    case '/moi': return <MoiPage />
    case '/equipe/rh': return <RhHubPage />
    case '/equipe/rh/salaries': return <RhPeoplePage />
    case '/settings/integrations/skello': return <RequirePermission perm="integration.read"><SkelloIntegrationPage /></RequirePermission>
    case '/security': return <SecurityHub />
    case '/parc': return <ParcHub />
    case '/communication': return <CommunicationHomePage />
    case '/infrastructure': return <InfrastructureHub />
    case '/alerts': return <Redirection to="/activite" />
    case '/audit': return <AuditPage />
    case '/profile': return <ProfilePage />
    case '/settings': return <SettingsHome />
    case '/settings/users': return <UsersPage />
    case '/settings/roles': return <RolesPage />
    case '/settings/services': return <ServicesPage />
    case '/settings/integrations': return <IntegrationsCatalogPage />
    case '/settings/security': return <SecurityPage />
    case '/settings/appearance': return <AppearancePage />
    case '/settings/general': return <GeneralPage />
    case '/settings/system': return <SystemPage />
    case '/settings/backups': return <BackupsPage />
    default: return <GlobalDashboardPage />
  }
}

/**
 * La liste des associations — ou, pour qui gère les soirées SANS consulter
 * les associations et arrive par l'alerte « à rattacher avant la nuit »
 * (`?vue=a-decider&cas=…`), Réglages Soirées › Billetteries, où la même liste
 * « À rattacher » l'attend, ouverte sur le cas (revue du 20/10/2026 : le lien
 * menait sinon à un refus). Tous les autres passent par la garde habituelle.
 */
function RouteListeAssociations({ navigations }: { navigations: number }) {
  const { permissions, loading } = usePermissions()
  const cible = loading ? null : redirectionADecider(permissions, window.location.search)
  useEffect(() => { if (cible) redirect(cible) }, [cible])
  if (cible) return null
  return <RequirePermission perm="association.read"><AssociationsListePage key={navigations} /></RequirePermission>
}

/** Ancienne adresse → nouvelle, sans empiler d'historique ni écran blanc. */
function Redirection({ to }: { to: string }) {
  useEffect(() => { redirect(to) }, [to])
  return null
}

export function App() {
  const path = usePath()
  if (path === '/pointeuse' || path.startsWith('/pointeuse/')) return <PointeuseKiosk />
  // « Autoriser LC Porte » : hors du shell (la carte des écrans de connexion), AVANT le monde auth.
  if (path === '/porte/connexion') return <PorteConnexionPage />
  if (isAuthPath(path)) return <AuthWorld path={path} />
  return (
    <AppShell>
      <GardeEcran chemin={path}>
        <RouteContent path={path} />
      </GardeEcran>
    </AppShell>
  )
}
